import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { ErpModule } from '@/api/platform.api';
import { detectTourProgress } from '@/core/tour/detectTourProgress';
import { QuickStartPanel } from '@/core/tour/QuickStartPanel';
import { TOUR_STEPS, type TourStepDefinition } from '@/core/tour/tourConfig';

const CHECKLIST_KEY = 'fattah_tour_checklist_v2';
const DISMISS_KEY = 'fattah_tour_dismissed_v2';

function readChecklist(module: ErpModule): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(`${CHECKLIST_KEY}_${module}`);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeChecklist(module: ErpModule, state: Record<string, boolean>) {
  localStorage.setItem(`${CHECKLIST_KEY}_${module}`, JSON.stringify(state));
}

function readDismissed(module: ErpModule): boolean {
  return localStorage.getItem(`${DISMISS_KEY}_${module}`) === '1';
}

function writeDismissed(module: ErpModule) {
  localStorage.setItem(`${DISMISS_KEY}_${module}`, '1');
}

type TourContextValue = {
  steps: TourStepDefinition[];
  completed: Record<string, boolean>;
  dismiss: () => void;
  visible: boolean;
  detecting: boolean;
  refresh: () => void;
};

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ module, children }: { module: ErpModule | null; children: ReactNode }) {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => (module ? readDismissed(module) : false));
  const [completed, setCompleted] = useState<Record<string, boolean>>(() =>
    module ? readChecklist(module) : {},
  );
  const [detecting, setDetecting] = useState(false);
  const detectTimerRef = useRef<number | null>(null);

  const steps = module ? (TOUR_STEPS[module] ?? []) : [];

  useEffect(() => {
    if (!module) return;
    setDismissed(readDismissed(module));
    setCompleted(readChecklist(module));
  }, [module]);

  const mergeDetected = useCallback(
    (detected: Record<string, boolean>) => {
      if (!module) return;
      setCompleted((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const step of steps) {
          if (detected[step.id] && !next[step.id]) {
            next[step.id] = true;
            changed = true;
          }
        }
        if (changed) {
          writeChecklist(module, next);
        }
        return changed ? next : prev;
      });
    },
    [module, steps],
  );

  const runDetection = useCallback(() => {
    if (!module || steps.length === 0) return;
    setDetecting(true);
    void detectTourProgress(module, location.pathname)
      .then(mergeDetected)
      .catch(() => undefined)
      .finally(() => setDetecting(false));
  }, [location.pathname, mergeDetected, module, steps.length]);

  useEffect(() => {
    runDetection();
    if (detectTimerRef.current) {
      window.clearInterval(detectTimerRef.current);
    }
    detectTimerRef.current = window.setInterval(runDetection, 45_000);
    return () => {
      if (detectTimerRef.current) {
        window.clearInterval(detectTimerRef.current);
      }
    };
  }, [runDetection]);

  const allDone = steps.length > 0 && steps.every((step) => completed[step.id]);
  const visible = Boolean(module && steps.length > 0 && !dismissed && !allDone);

  const dismiss = useCallback(() => {
    if (module) writeDismissed(module);
    setDismissed(true);
  }, [module]);

  const value = useMemo(
    () => ({
      steps,
      completed,
      dismiss,
      visible,
      detecting,
      refresh: runDetection,
    }),
    [steps, completed, dismiss, visible, detecting, runDetection],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {visible && (
        <QuickStartPanel steps={steps} completed={completed} detecting={detecting} onDismiss={dismiss} />
      )}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  return (
    ctx ?? {
      steps: [],
      completed: {},
      dismiss: () => undefined,
      visible: false,
      detecting: false,
      refresh: () => undefined,
    }
  );
}

export type { TourStepDefinition };
